    import React, { useEffect, useState } from "react";
    import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from "recharts";
    import { useSession } from "../../../SessionContext";
    import { ScreenConfig } from "../Sequences";
    import { useLanguage } from "../../../../context/LanguageContext";
    import { DefaultOption } from "../../../../translations/experiments/Default";
    import { colors, commonStyles } from "../../../../components/colors";

    const barColors = [colors.lichtBlau, colors.sienna];

    const cardStyle: React.CSSProperties = {
        ...commonStyles.card,
        margin: 0,
        minWidth: 350,
        maxWidth: 420,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        minHeight: 200
    };

    const ResultScreenCrossoverDefaultComponent: React.FC = () => {
        const { getGroupData } = useSession();
        const { language } = useLanguage();
        const t = DefaultOption[language].results.default;

        const [aligned, setAligned] = useState(0);
        const [misaligned, setMisaligned] = useState(0);
        const [total, setTotal] = useState(0);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
            setLoading(true);
            getGroupData().then((data: any[]) => {
                let alignedWithDefault = 0, againstDefault = 0, sum = 0;
                data?.forEach((dat: any) => {
                    if (dat?.CrossoverDefault) {
                        Object.values(dat.CrossoverDefault).forEach((entry: any) => {
                            if (entry && Array.isArray(entry.selections) && entry.selections.length > 0) {
                                const sel = entry.selections[entry.selections.length - 1];
                                if (
                                    sel &&
                                    (sel.defaultOption === 0 || sel.defaultOption === 1) &&
                                    (sel.choice === 0 || sel.choice === 1)
                                ) {
                                    sum += 1;
                                    if (sel.choice === sel.defaultOption) alignedWithDefault++;
                                    else againstDefault++;
                                }
                            }
                        });
                    }
                });
                setAligned(alignedWithDefault);
                setMisaligned(againstDefault);
                setTotal(sum);
                setLoading(false);
            }).catch(() => setLoading(false));
        }, [getGroupData]);

        const data = [
            { name: t.defaultChosen, value: aligned },
            { name: t.againstDefault, value: misaligned }
        ];

        return (
            <div className="experiment-container" style={commonStyles.container}>
                <h2 style={{
                    ...commonStyles.heading,
                    margin: "0 0 24px 0",
                    fontSize: 28,
                    fontWeight: 700,
                }}>
                    {t.title || "Default Option – Global Results"}
                </h2>
                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 30
                }}>
                    <div style={cardStyle}>
                        {loading ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 18, textAlign: "center", marginTop: 60 }}>
                                {t.loading || "Loading global results…"}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={data} barCategoryGap={60}>
                                    <XAxis dataKey="name" interval={0} tick={{ fontSize: 16 }} />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" barSize={60}>
                                        <Cell fill={barColors[0]} />
                                        <Cell fill={barColors[1]} />
                                        <LabelList
                                            dataKey="value"
                                            position="top"
                                            formatter={(v: number, entry: any, idx: number) => {
                                                return total > 0 ? `${v} (${Math.round(v * 100 / total)}%)` : v;
                                            }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
                {!loading &&
                    <div style={{
                        textAlign: "center",
                        margin: "22px 0 0 0",
                        fontSize: 15,
                        color: colors.textMedium
                    }}>
                        {total > 0
                            ? (language === "de"
                                ? <>Insgesamt <b>{aligned}</b> ({Math.round(aligned * 100 / total)}%) haben die Voreinstellung gewählt, <b>{misaligned}</b> dagegen entschieden.</>
                                : <>A total of <b>{aligned}</b> ({Math.round(aligned * 100 / total)}%) chose the preset option, <b>{misaligned}</b> went against it.</>)
                            : ""}
                    </div>
                }
            </div>
        );
    };

    export const ResultScreenCrossoverDefault: ScreenConfig = {
        component: ResultScreenCrossoverDefaultComponent,
        defaultResult: {},
        canContinueDefault: true,
    };